import * as React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Button,
  Form,
  FormField,
  FormLabel,
  FormItem,
  FormControl,
  FormMessage,
  Input,
  PasswordInput,
  cn,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  emailLabel,
  emailPlaceholder,
  emailRequiredValidationMessage,
  forgotPasswordText,
  passwordLabel,
  passwordRequiredValidationMessage,
  signInButtonLabel,
} from '../../../../../stringsManager';
import { MailIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

// When `identifierLabel` is supplied the field accepts any non-empty
// identifier (e.g. an email OR a student ID); otherwise it stays strict email.
const buildFormSchema = (isIdentifier: boolean) =>
  z.object({
    email: isIdentifier
      ? z.string().min(1, { message: emailRequiredValidationMessage })
      : z
          .string()
          .min(1, { message: emailRequiredValidationMessage })
          .email(),
    password: z
      .string({ required_error: passwordRequiredValidationMessage })
      .min(1),
  });

const formSchema = buildFormSchema(false);

type SignInFormValues = z.infer<typeof formSchema>;

interface SignInFormProps {
  onSubmit: (values: SignInFormValues) => void;
  loading: boolean;
  className?: string;
  resetPasswordRoute: string;
  defaultValues?: Partial<SignInFormValues>;
  searchParams: URLSearchParams;
  /**
   * Overrides the email field label (e.g. "Email or Student ID"). When set,
   * the field also accepts any non-empty identifier instead of requiring a
   * valid email. Defaults to the shared `emailLabel` + email validation.
   */
  identifierLabel?: string;
  /** Restyles the submit button. Defaults to today's plain `w-full`. */
  submitButtonClassName?: string;
  /** Restyles both field labels. Defaults to today's `font-normal`. */
  labelClassName?: string;
  /**
   * Restyles both Input fields. `Input` merges `className` by plain string
   * concatenation, not `cn()` - only pass classes that add cleanly rather
   * than ones meant to override an existing utility (e.g. `h-11`, not a
   * second conflicting `h-*`).
   */
  inputClassName?: string;
  /**
   * Restyles the "Forgot password" link. Defaults to today's `text-primary`
   * (a normal link color); FIT overrides it to an off-black to match the
   * field labels instead.
   */
  forgotPasswordLinkClassName?: string;
}

export const SignInForm = ({
  className,
  loading,
  onSubmit,
  resetPasswordRoute,
  defaultValues,
  searchParams,
  identifierLabel,
  submitButtonClassName,
  labelClassName,
  inputClassName,
  forgotPasswordLinkClassName,
  ...props
}: SignInFormProps) => {
  const schema = React.useMemo(
    () => buildFormSchema(!!identifierLabel),
    [identifierLabel]
  );

  const form = useForm<SignInFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: defaultValues?.email,
      password: defaultValues?.password,
    },
  });

  return (
    <div className={cn('grid gap-4 text-foreground', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={cn('font-normal', labelClassName)}>
                  {identifierLabel ?? emailLabel}
                </FormLabel>
                <FormControl>
                  <Input
                    disabled={loading}
                    placeholder={emailPlaceholder}
                    {...field}
                    endAdornment={<MailIcon />}
                    className={inputClassName}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={cn('font-normal', labelClassName)}>
                  {passwordLabel}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    disabled={loading}
                    {...field}
                    className={inputClassName}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Link
              to={resetPasswordRoute}
              className={cn(
                'text-sm text-primary leading-tight',
                forgotPasswordLinkClassName
              )}
            >
              {forgotPasswordText}
            </Link>
          </div>

          <Button
            disabled={loading}
            type="submit"
            className={cn('w-full', submitButtonClassName)}
          >
            {signInButtonLabel}
          </Button>
        </form>
      </Form>
    </div>
  );
};
